import {
  doc,
  getDoc,
  setDoc,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { validarCuentaActiva } from "./accountingCore";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const normalizeCode = (v) => cleanText(v).replace(/[^\d]/g, "");

export const CONFIG_DEFAULTS = {
  cuenta_clientes: "12121",
  cuenta_ventas: "701101",
  cuenta_igv: "401111",
  cuenta_proveedores: "421201",
  cuenta_bancos: "104101",
  cuenta_caja: "101101",
  cuenta_retiro_socio: "142101",
  cuenta_ganancia_cambio: "776101",
  cuenta_perdida_cambio: "676101",
};

/**
 * Retrieves the accounting configuration for a company.
 * Returns default values if no custom configuration exists.
 */
export const obtenerConfiguracionContable = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) throw new Error("RUC de empresa inválido para obtener configuración contable.");

  const ref = doc(db, CONT_COLLECTIONS.CONFIG_CONTABLE, ruc);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const data = snap.data();
    return {
      cuenta_clientes: cleanText(data.cuenta_clientes || CONFIG_DEFAULTS.cuenta_clientes),
      cuenta_ventas: cleanText(data.cuenta_ventas || CONFIG_DEFAULTS.cuenta_ventas),
      cuenta_igv: cleanText(data.cuenta_igv || CONFIG_DEFAULTS.cuenta_igv),
      cuenta_proveedores: cleanText(data.cuenta_proveedores || CONFIG_DEFAULTS.cuenta_proveedores),
      cuenta_bancos: cleanText(data.cuenta_bancos || CONFIG_DEFAULTS.cuenta_bancos),
      cuenta_caja: cleanText(data.cuenta_caja || CONFIG_DEFAULTS.cuenta_caja),
      cuenta_retiro_socio: cleanText(data.cuenta_retiro_socio || CONFIG_DEFAULTS.cuenta_retiro_socio),
      cuenta_ganancia_cambio: cleanText(data.cuenta_ganancia_cambio || CONFIG_DEFAULTS.cuenta_ganancia_cambio),
      cuenta_perdida_cambio: cleanText(data.cuenta_perdida_cambio || CONFIG_DEFAULTS.cuenta_perdida_cambio),
    };
  }

  return { ...CONFIG_DEFAULTS };
};

/**
 * Validates and saves custom accounting configuration for a company.
 * All accounts must exist and be ACTIVE in the company's PCGE.
 */
export const guardarConfiguracionContable = async ({ rucEmpresa, config }) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) throw new Error("RUC de empresa inválido para guardar configuración contable.");
  if (!config || typeof config !== "object") throw new Error("Configuración contable inválida.");

  const payload = {};
  const keys = Object.keys(CONFIG_DEFAULTS);

  for (const key of keys) {
    const rawVal = cleanText(config[key]);
    const val = rawVal || CONFIG_DEFAULTS[key];
    const cleanVal = normalizeCode(val);

    // Validate that the account exists and is active in the company's PCGE
    const esActiva = await validarCuentaActiva(ruc, cleanVal);
    if (!esActiva) {
      throw new Error(`Validación fallida: La cuenta ${cleanVal} para el parámetro ${key} no está activa o no existe en el PCGE.`);
    }

    payload[key] = cleanVal;
  }

  const ref = doc(db, CONT_COLLECTIONS.CONFIG_CONTABLE, ruc);
  await setDoc(
    ref,
    {
      ...payload,
      ruc_empresa: ruc,
      updated_at: serverTimestamp(),
    },
    { merge: true }
  );

  return true;
};

/**
 * Resolves the configuration parameters, validating their active status in the company's PCGE.
 * Falls back to default values if the resolved custom values are inactive or missing.
 */
export const resolverCuentasContables = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  const config = await obtenerConfiguracionContable(ruc);
  const resolved = {};

  const keys = Object.keys(CONFIG_DEFAULTS);
  for (const key of keys) {
    let code = normalizeCode(config[key]);
    let esActiva = await validarCuentaActiva(ruc, code);

    if (!esActiva) {
      const fallbackCode = CONFIG_DEFAULTS[key];
      const fallbackActiva = await validarCuentaActiva(ruc, fallbackCode);
      if (!fallbackActiva) {
        throw new Error(`Falla de consistencia: La cuenta configurada ${code} y la cuenta default ${fallbackCode} para ${key} no están activas en el PCGE.`);
      }
      code = fallbackCode;
    }

    resolved[key] = code;
  }

  return resolved;
};
